import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import API from '../config/api';
import { downloadInvoicePdf } from '../utils/invoicePdf';

export default function OrderSuccess() {
  const location = useLocation();
  const { token } = useContext(AuthContext);
  const { lang, t, formatPrice } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [orderBundle, setOrderBundle] = useState(null);

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const orderPublicId = params.get('order') || '';
  const queryEmail = params.get('email') || '';
  const queryPhone = params.get('phone') || '';

  useEffect(() => {
    const load = async () => {
      if (!orderPublicId) {
        setError('Order reference is missing.');
        setLoading(false);
        return;
      }

      try {
        const localCtxRaw = sessionStorage.getItem('kh_last_order_context');
        const localCtx = localCtxRaw ? JSON.parse(localCtxRaw) : null;
        const emailFromSession = localCtx?.orderPublicId === orderPublicId ? (localCtx.email || '') : '';
        const phoneFromSession = localCtx?.orderPublicId === orderPublicId ? (localCtx.phone || '') : '';
        const email = emailFromSession || queryEmail;
        const phone = phoneFromSession || queryPhone;

        const headers = {};
        if (token) headers.Authorization = `Bearer ${token}`;

        const res = await fetch(API.orders.get(orderPublicId, email, phone), { headers });
        const data = await res.json();

        if (!res.ok || data.status !== 'success') {
          throw new Error(data.message || 'Unable to load order details.');
        }

        setOrderBundle(data.data);
      } catch (err) {
        setError(err.message || 'Unable to load order details.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [orderPublicId, queryEmail, queryPhone, token]);

  const downloadBill = () => {
    downloadInvoicePdf(orderBundle);
  };

  return (
    <main className="kh-order-success-page" style={{ minHeight: '100vh', background: '#f5f3ee', padding: '56px 20px' }}>
      <section style={{ maxWidth: '980px', margin: '0 auto' }}>
        {loading && <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #ece6d9', padding: '20px' }}>Loading order details...</div>}

        {!loading && error && (
          <div style={{ background: '#fff2f2', border: '1px solid #f5c2c2', borderRadius: '12px', padding: '20px' }}>
            <p style={{ margin: '0 0 12px', color: '#9c1d1d' }}>{error}</p>
            <Link to="/shop" className="btn-primary">Continue Shopping</Link>
          </div>
        )}

        {!loading && !error && orderBundle && (
          <>
            <div style={{ background: '#fff', border: '1px solid #ece6d9', borderRadius: '12px', padding: '22px', marginBottom: '14px' }}>
              <p style={{ margin: 0, color: '#0e7d3f', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', fontSize: '11px' }}>{lang === 'de' ? t('orderSuccess.successLabel') : 'Order placed successfully'}</p>
              <h1 style={{ margin: '8px 0 8px', fontFamily: 'Noto Serif, Georgia, serif', fontWeight: 400, color: '#1f1d16' }}>{lang === 'de' ? t('orderSuccess.thankYou') : 'Thank you for your purchase'}</h1>
              <p style={{ margin: 0, color: '#5d584d' }}>{lang === 'de' ? t('orderSuccess.orderId') : 'Order ID'}: <strong>{orderBundle.order?.order_public_id || orderBundle.order?.id}</strong></p>
            </div>

            <div style={{ background: '#fff', border: '1px solid #ece6d9', borderRadius: '12px', padding: '22px', marginBottom: '14px' }}>
              <h3 style={{ marginTop: 0, color: '#1f1d16' }}>{lang === 'de' ? t('orderSuccess.billSummary') : 'Bill Summary'}</h3>
              <div style={{ display: 'grid', gap: '8px' }}>
                {(orderBundle.items || []).map((item, idx) => (
                  <div key={idx} className="kh-order-success-item" style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f0ebdf', paddingBottom: '8px' }}>
                    <span style={{ color: '#4c473c' }}>{item.product_name || 'Product'} {item.selected_size ? `(${item.selected_size})` : ''} × {item.quantity}</span>
                    <span style={{ color: '#1f1d16', fontWeight: 600 }}>{formatPrice(Number(item.locked_price || 0) * Number(item.quantity || 0)).display}</span>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: '14px', display: 'flex', justifyContent: 'space-between', fontSize: '18px', fontWeight: 700, color: '#1f1d16' }}>
                <span>{lang === 'de' ? t('cart.total') : 'Total'}</span>
                <span>{formatPrice(orderBundle.order?.total_amount).display}</span>
              </div>

              <div className="kh-order-success-actions" style={{ marginTop: '18px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button onClick={downloadBill} style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #d4c7a9', background: '#fff', color: '#7d5e12', fontSize: '12px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', cursor: 'pointer' }}>{lang === 'de' ? t('orderSuccess.downloadBill') : 'Download Bill'}</button>
                <Link to={`/orders/${encodeURIComponent(orderBundle.order?.order_public_id || '')}`} style={{ textDecoration: 'none', padding: '10px 16px', borderRadius: '8px', border: '1px solid #ddd', color: '#3f3f3f', fontSize: '12px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' }}>{lang === 'de' ? t('orderSuccess.orderDetails') : 'Order Details'}</Link>
                <Link to="/orders" style={{ textDecoration: 'none', padding: '10px 16px', borderRadius: '8px', border: '1px solid #ddd', color: '#3f3f3f', fontSize: '12px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' }}>{lang === 'de' ? t('orders.title') : 'My Orders'}</Link>
              </div>
            </div>

            <div style={{ background: '#fff', border: '1px solid #ece6d9', borderRadius: '12px', padding: '16px' }}>
              <p style={{ margin: 0, color: '#5d584d' }}>Payment status: <strong style={{ textTransform: 'uppercase' }}>{orderBundle.payment?.status || orderBundle.order?.status || 'pending'}</strong></p>
              <p style={{ margin: '8px 0 0', color: '#5d584d' }}>Confirmation details are sent to: <strong>{orderBundle.order?.guest_email || 'your email'}</strong></p>
            </div>
          </>
        )}
      </section>
      <style>{`
        @media (max-width: 700px) {
          .kh-order-success-page { padding: 28px 12px !important; }
          .kh-order-success-item { flex-direction: column; gap: 5px; }
          .kh-order-success-actions > button,
          .kh-order-success-actions > a {
            width: 100%;
            text-align: center;
          }
        }
      `}</style>
    </main>
  );
}
